import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { store } from 'components/Store';
import useRegulationDocumentData from 'components/Dashboard/Regulations/useRegulationDocumentData';
import RegulationsDocumentOverviewTab from 'components/Dashboard/Regulations/RegulationsDocumentOverviewTab';
import RegulationsDocumentCommentsTab from 'components/Dashboard/Regulations/RegulationsDocumentCommentsTab';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import ThumbsDown from 'components/Common/ThumbsDown';
import Share from 'components/Dashboard/components/Share';
import time from 'utils/time';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import isValidURL from 'components/Dashboard/utilities/isValidURL';

const TAB_HASH = {
  overview: 'overview',
  comments: 'comments',
};

const tabFromHash = () => {
  const key = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
  return TAB_HASH[key] ? key : 'overview';
};

const RegulationDocumentContentPage = ({ currentNew, contentContainer, hideSummary, reportWidget }) => {
  const { dispatch } = useContext(store);
  const navigate = useNavigate();
  const location = useLocation();
  const { comments, commentsTotal, isCommentsLoading, loadRegulationDocumentComments } = useRegulationDocumentData({
    commentsOnly: true,
  });
  const [activeTab, setActiveTab] = useState(tabFromHash);
  const [heightContainer, containerRef] = useHeightContainer();

  const titleSection = useRef(null);

  // PDF scaling for reportWidget happens in calculateWidgetHeight (useSpecificUpdateCalculations.js), not here.
  const { showFixedTitle, titleStyles } = useTitleScrolledStyles({ contentContainer, titleSection, leftValue: 0 });
  const { createMarkup: createMarkupHighlighted } = useCreateMarkup();

  useEffect(() => {
    setActiveTab(tabFromHash());
  }, [location.hash]);

  const selectTab = (tab) => {
    navigate({ hash: TAB_HASH[tab] }, { replace: true });
  };

  const commentsTitle = `Comments${commentsTotal > 0 ? ` (${commentsTotal})` : ''}`;
  const { id, title, objectId, objectType, canonicalUrl, body, summary, agency, documentType, postedDate, sourceUrl } =
    currentNew ?? {};
  const item = { id, title, objectID: objectId, type: objectType };

  if (reportWidget) {
    return (
      <div className='p-3 p-lg-4'>
        <p className='heading-date mb-0'>
          {!!agency && (
            <>
              {agency}
              <span className='dot'></span>
            </>
          )}
          {!!documentType && (
            <>
              {documentType}
              <span className='dot'></span>
            </>
          )}
          {!!postedDate && <span className='date'>{time(postedDate).formatGeneralDate()}</span>}
          {!!sourceUrl && isValidURL(sourceUrl) && (
            <>
              <span className='dot' />
              <a className='mb-0' href={sourceUrl} target='_blank' rel='noopener noreferrer'>
                Source: {new URL(sourceUrl).hostname}
              </a>
            </>
          )}
        </p>
        {(body || summary) && (
          <div className='pt-3 news-content-in parliamentary-content-in context committee-publication-content'>
            <div className='debate-item-contributiondebateitem'>
              <div
                className='explanatory-container'
                style={{ overflow: 'hidden' }}
                dangerouslySetInnerHTML={createMarkup(body ?? summary)}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className='section-title pb-2 regulations-content-title p-3 p-lg-4' ref={titleSection}>
        <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkupHighlighted(title)} />
        <div className='row mx-0 justify-content-between align-items-center'>
          <p className='heading-date mb-0'>
            {!!agency && (
              <>
                {agency}
                <span className='dot'></span>
              </>
            )}
            {!!documentType && (
              <>
                {documentType}
                <span className='dot'></span>
              </>
            )}
            {!!postedDate && <span className='date'>{time(postedDate).formatGeneralDate()}</span>}
            {!!sourceUrl && (
              <>
                <span className='dot' />
                <a className='legislation-source mb-0' href={sourceUrl} target='_blank' rel='noopener noreferrer'>
                  Go to source
                </a>
              </>
            )}
          </p>
          <div className='flex-centered'>
            <ThumbsDown
              item={{ objectID: currentNew?.objectId }}
              nameSection='RegulationDocuments'
              show={!!currentNew?.keywordsFromLists}
              contentPage={true}
              alreadyMarked={!!currentNew?.feedback}
              keywordsFromLists={currentNew?.keywordsFromLists}
              thick={true}
            />
            <Share
              item={item}
              id={id}
              title={title}
              link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
            />
          </div>
        </div>
        <div className='pt-3' ref={containerRef}>
          {currentNew?.regulationDocketId ? (
            <Link
              to={`/regulation-docket/${currentNew.regulationDocketId}/${normalize(currentNew.docketTitle)}`}
              className='general-button hovered-button border rounded-pill px-3 py-1 mr-3 d-inline-block reset-link link-main-blue'
              onClick={() => dispatch({ type: 'MODIFY_SECTION', parameter: 'clickedOnContentUrl', value: null })}
            >
              Go to docket
            </Link>
          ) : (
            <button className='general-button hovered-button border rounded-pill px-3 py-1 mr-3' disabled>
              Go to docket
            </button>
          )}
          <button
            className={`general-button ${activeTab === 'overview' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1 mr-3`}
            onClick={() => selectTab('overview')}
          >
            Document overview
          </button>
          <button
            className={`general-button ${activeTab === 'comments' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1`}
            onClick={() => selectTab('comments')}
          >
            {commentsTitle}
          </button>
        </div>
      </div>
      <div
        className={`content-item-scrolled-title main-sticky section-title bg-white parliamentary-scrolled-title ${
          showFixedTitle ? 'item-scrolled-title-visible' : ''
        }`}
        style={titleStyles}
      >
        <div className='d-flex justify-content-between scrolled-title-container'>
          <h3
            className='main-title news-title bg-white content-item-title'
            dangerouslySetInnerHTML={createMarkupHighlighted(title)}
          />
          <ThumbsDown
            item={{ objectID: currentNew?.objectId }}
            nameSection='RegulationDocuments'
            show={!!currentNew?.keywordsFromLists}
            contentPage={true}
            alreadyMarked={!!currentNew?.feedback}
            keywordsFromLists={currentNew?.keywordsFromLists}
            thick={true}
          />
          <Share
            item={item}
            id={id}
            title={title}
            link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
          />
        </div>
      </div>
      {activeTab === 'overview' && !!currentNew && (
        <RegulationsDocumentOverviewTab currentNew={currentNew} heightContainer={heightContainer} />
      )}
      {activeTab === 'comments' && (
        <RegulationsDocumentCommentsTab
          comments={comments}
          commentsTotal={commentsTotal}
          isLoading={isCommentsLoading}
          loadRegulationDocumentComments={loadRegulationDocumentComments}
          heightContainer={heightContainer}
        />
      )}
    </>
  );
};

export default RegulationDocumentContentPage;
